import { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAdminStore } from '../../store/adminStore'
import Spinner from '../../components/common/Spinner'

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  active:    { bg: '#22c55e22', color: '#22c55e' },
  suspended: { bg: '#f59e0b22', color: '#f59e0b' },
  banned:    { bg: '#ef444422', color: '#ef4444' },
}

const SUB_STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  pending:  { bg: '#f59e0b22', color: '#f59e0b' },
  verified: { bg: '#22c55e22', color: '#22c55e' },
  rejected: { bg: '#ef444422', color: '#ef4444' },
}

function StatBadge({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-xl p-3 text-center" style={{ background: 'var(--app-card-bg)' }}>
      <p className="text-xl font-bold" style={{ color: 'var(--tg-theme-text-color)' }}>
        {value}
      </p>
      <p className="text-xs opacity-50 mt-0.5" style={{ color: 'var(--tg-theme-text-color)' }}>
        {label}
      </p>
    </div>
  )
}

export default function AdminWorkerDetailPage() {
  const { workerId } = useParams<{ workerId: string }>()
  const navigate = useNavigate()
  const { workerDetail, isLoading, error, fetchWorkerDetail } = useAdminStore()

  useEffect(() => {
    if (workerId) fetchWorkerDetail(workerId)
  }, [workerId]) // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner />
      </div>
    )
  }

  if (error || !workerDetail) {
    return (
      <div className="p-4 text-center py-12">
        <p className="text-sm" style={{ color: '#ef4444' }}>
          {error || 'Worker not found'}
        </p>
        <button
          className="mt-4 text-sm"
          style={{ color: 'var(--tg-theme-link-color)' }}
          onClick={() => navigate('/admin/workers')}
        >
          ← Back to workers
        </button>
      </div>
    )
  }

  const w = workerDetail
  const statusColor = STATUS_COLORS[w.status] ?? STATUS_COLORS.active

  return (
    <div className="p-4 space-y-4 pb-8">
      {/* Back */}
      <button
        className="text-sm"
        style={{ color: 'var(--tg-theme-link-color)' }}
        onClick={() => navigate('/admin/workers')}
      >
        ← Workers
      </button>

      {/* Header */}
      <div className="rounded-xl p-4 space-y-2" style={{ background: 'var(--app-card-bg)' }}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-base font-bold" style={{ color: 'var(--tg-theme-text-color)' }}>
              {w.first_name || w.telegram_username || 'Worker'}
              {w.last_name ? ` ${w.last_name}` : ''}
            </p>
            {w.telegram_username && (
              <p className="text-sm opacity-60" style={{ color: 'var(--tg-theme-text-color)' }}>
                @{w.telegram_username}
              </p>
            )}
          </div>
          <span
            className="text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0"
            style={{ background: statusColor.bg, color: statusColor.color }}
          >
            {w.status}
          </span>
        </div>

        <div className="flex gap-4 pt-1">
          <div>
            <p className="text-xs opacity-50" style={{ color: 'var(--tg-theme-text-color)' }}>Level</p>
            <p className="text-sm font-semibold" style={{ color: 'var(--tg-theme-text-color)' }}>
              {w.level} ({w.xp} XP)
            </p>
          </div>
          <div>
            <p className="text-xs opacity-50" style={{ color: 'var(--tg-theme-text-color)' }}>Coins</p>
            <p className="text-sm font-semibold" style={{ color: 'var(--tg-theme-text-color)' }}>
              {w.coins_balance} 🪙 ({w.coins_total_earned} earned)
            </p>
          </div>
          <div>
            <p className="text-xs opacity-50" style={{ color: 'var(--tg-theme-text-color)' }}>Streak</p>
            <p className="text-sm font-semibold" style={{ color: 'var(--tg-theme-text-color)' }}>
              {w.streak_days} 🔥
            </p>
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-2">
        <StatBadge label="Items Found" value={w.items_found_total} />
        <StatBadge label="Items Verified" value={w.items_verified_total} />
        <StatBadge label="Approval Rate" value={`${w.approval_rate}%`} />
        <StatBadge label="Tasks Done" value={w.total_tasks_completed} />
      </div>

      {/* Submissions */}
      <div className="space-y-2">
        <p className="text-xs font-semibold opacity-50 uppercase tracking-wider" style={{ color: 'var(--tg-theme-text-color)' }}>
          Recent Submissions ({w.submissions.length})
        </p>

        {w.submissions.length === 0 && (
          <p className="text-sm text-center opacity-40 py-8" style={{ color: 'var(--tg-theme-text-color)' }}>
            No submissions yet
          </p>
        )}

        {w.submissions.map((sub) => {
          const sc = SUB_STATUS_COLORS[sub.status] ?? SUB_STATUS_COLORS.pending
          const isFound = sub.submission_type === 'found'

          return (
            <div
              key={sub.id}
              className="rounded-xl p-3"
              style={{ background: 'var(--app-card-bg)' }}
            >
              <div className="flex gap-3">
                {/* Thumbnail */}
                <div
                  className="w-12 h-12 rounded-xl flex-shrink-0 overflow-hidden flex items-center justify-center text-xl"
                  style={{ background: 'var(--app-page-bg)' }}
                >
                  {sub.thumbnail_url ? (
                    <img
                      src={sub.thumbnail_url}
                      alt=""
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <span className="opacity-30">📷</span>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium truncate" style={{ color: 'var(--tg-theme-text-color)' }}>
                      {sub.product_name || <span className="opacity-40">Unknown product</span>}
                    </p>
                    <span
                      className="text-xs px-1.5 py-0.5 rounded-full font-medium flex-shrink-0"
                      style={{ background: sc.bg, color: sc.color }}
                    >
                      {sub.status}
                    </span>
                  </div>

                  {sub.shop_name && (
                    <p className="text-xs opacity-60 mt-0.5" style={{ color: 'var(--tg-theme-text-color)' }}>
                      🏪 {sub.shop_name}
                    </p>
                  )}

                  <div className="flex items-center gap-3 mt-0.5">
                    <span
                      className="text-xs"
                      style={{ color: isFound ? '#22c55e' : '#ef4444' }}
                    >
                      {isFound ? '✓ Found' : '✗ Not Found'}
                    </span>
                    {sub.price_uzs && (
                      <span className="text-xs font-medium" style={{ color: 'var(--tg-theme-text-color)' }}>
                        {Number(sub.price_uzs).toLocaleString()} UZS
                      </span>
                    )}
                    {sub.created_at && (
                      <span className="text-xs opacity-40 ml-auto" style={{ color: 'var(--tg-theme-text-color)' }}>
                        {new Date(sub.created_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
